import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createReleaseFolder,
  updateReleaseFolder,
  deleteReleaseFolder,
} from "./release-folders.api";

import type { ReleaseFolderTree } from "@/common/types/Cycle";

const FOLDER_KEY = ["releaseFolders"];

export const useCreateReleaseFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createReleaseFolder,
    onMutate: async (newFolder) => {
      await queryClient.cancelQueries({ queryKey: FOLDER_KEY });
      const queries = queryClient.getQueriesData<ReleaseFolderTree[]>({
        queryKey: FOLDER_KEY,
      });
      // Optimistically append the new folder with a temp negative id
      queries.forEach(([key, old]) => {
        if (!old) return;
        const now = new Date().toISOString();
        const optimistic: ReleaseFolderTree = {
          id: -Date.now(),
          name: newFolder.name,
          workspace_version_id: newFolder.workspace_version_id,
          parent_id: newFolder.parent_id ?? null,
          position: old.length,
          created_at: now,
          updated_at: now,
          children: [],
          cycles: [],
        };
        queryClient.setQueryData<ReleaseFolderTree[]>(key, [
          ...old,
          optimistic,
        ]);
      });
      return { queries };
    },
    onError: (_err, _vars, ctx) => {
      // Roll back to previous state
      ctx?.queries.forEach(([key, old]) => {
        if (old) queryClient.setQueryData(key, old);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: FOLDER_KEY });
    },
  });
};

export const useUpdateReleaseFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      folderId,
      payload,
    }: {
      folderId: number;
      payload: Parameters<typeof updateReleaseFolder>[1];
    }) => updateReleaseFolder(folderId, payload),
    onMutate: async ({ folderId, payload }) => {
      await queryClient.cancelQueries({ queryKey: FOLDER_KEY });
      const queries = queryClient.getQueriesData<ReleaseFolderTree[]>({
        queryKey: FOLDER_KEY,
      });
      // Optimistically update the folder name in the cache
      queries.forEach(([key, old]) => {
        if (!old) return;
        queryClient.setQueryData<ReleaseFolderTree[]>(
          key,
          old.map((f) => (f.id === folderId ? { ...f, ...payload } : f)),
        );
      });
      return { queries };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.queries.forEach(([key, old]) => {
        if (old) queryClient.setQueryData(key, old);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: FOLDER_KEY });
    },
  });
};

export const useDeleteReleaseFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (folderId: number) => deleteReleaseFolder(folderId),
    onMutate: async (folderId) => {
      await queryClient.cancelQueries({ queryKey: FOLDER_KEY });
      const queries = queryClient.getQueriesData<ReleaseFolderTree[]>({
        queryKey: FOLDER_KEY,
      });
      // Optimistically remove the folder
      queries.forEach(([key, old]) => {
        if (!old) return;
        queryClient.setQueryData<ReleaseFolderTree[]>(
          key,
          old.filter((f) => f.id !== folderId),
        );
      });
      return { queries };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.queries.forEach(([key, old]) => {
        if (old) queryClient.setQueryData(key, old);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: FOLDER_KEY });
    },
  });
};
